/**
 * CalibrationAgreement — per-dimension agreement rate visualization (AC-10.4).
 * Shows Bedrock vs human agreement within ±1 level. Green indicator when ≥80%.
 */

import React from 'react';

const TARGET_THRESHOLD = 80;
const DIMENSION_NAMES = [
  'Discovery Planning',
  'Problem Framing',
  'Hypothesis Validation',
  'Solution Exploration',
  'User Validation',
  'Delivery Handoff',
] as const;

interface CalibrationAgreementProps {
  overall: number;
  perDimension: number[];
  sampleSize: number;
}

function AgreementBar({ rate, label }: { rate: number; label: string }): React.JSX.Element {
  const color = rate >= TARGET_THRESHOLD
    ? 'var(--genai-sem-success)'
    : 'var(--genai-sem-warning)';

  return (
    <div style={{ marginBottom: '8px' }} data-testid={`agreement-bar-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '13px', color: 'var(--genai-sem-text-secondary)' }}>{label}</span>
        <span style={{ fontSize: '13px', fontWeight: 600, color }}>{rate}%</span>
      </div>
      <div style={{ height: '6px', background: 'var(--genai-sem-border)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${rate}%`, background: color, borderRadius: '3px', transition: 'width 0.3s ease' }} />
      </div>
    </div>
  );
}

export function CalibrationAgreement({ overall, perDimension, sampleSize }: CalibrationAgreementProps): React.JSX.Element {
  const overallColor = overall >= TARGET_THRESHOLD
    ? 'var(--genai-sem-success)'
    : 'var(--genai-sem-warning)';

  return (
    <div className="card" data-testid="calibration-agreement">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 style={{ fontSize: 'var(--genai-text-base)', fontWeight: 600, margin: 0 }}>Agreement Rate</h3>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: overallColor, lineHeight: 1 }}>
            {overall}%
          </div>
          <div style={{ fontSize: '12px', color: 'var(--genai-sem-text-secondary)' }}>
            overall · n={sampleSize}
          </div>
        </div>
      </div>

      {overall >= TARGET_THRESHOLD && (
        <div
          data-testid="threshold-met"
          style={{
            padding: '8px 12px',
            background: 'var(--genai-sem-success-bg)',
            border: '1px solid var(--genai-sem-success)',
            borderRadius: '4px',
            color: 'var(--genai-sem-success)',
            fontSize: '12px',
            marginBottom: '16px',
          }}
        >
          ≥80% threshold met — scoring is well-calibrated
        </div>
      )}

      <div>
        {perDimension.map((rate, i) => (
          <AgreementBar
            key={i}
            rate={rate}
            label={DIMENSION_NAMES[i] ?? `Dimension ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
